import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import Chip from '@mui/material/Chip'
import Badge from '@mui/material/Badge'
import Tooltip from '@mui/material/Tooltip'
import { navigationSections } from '../../config/navigation'

function isPathActive(pathname, to) {
  if (to === '/dashboard') return pathname === '/dashboard' || pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * @param {{ collapsed: boolean, queue?: Record<string, number> }} props
 */
export default function SidebarNav({ collapsed, queue = {} }) {
  const { pathname } = useLocation()
  const [openMap, setOpenMap] = React.useState(() => {
    const init = {}
    navigationSections.forEach((s) => {
      if (s.defaultOpen) init[s.title] = true
    })
    return init
  })

  React.useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev }
      navigationSections.forEach((section) => {
        if (!section.items) return
        if (section.items.some((it) => isPathActive(pathname, it.to))) {
          next[section.title] = true
        }
      })
      return next
    })
  }, [pathname])

  const toggle = (title) => {
    setOpenMap((m) => ({ ...m, [title]: !m[title] }))
  }

  return (
    <Box sx={{ py: 1, px: collapsed ? 0.5 : 0 }}>
      {navigationSections.map((section) => {
        const Icon = section.icon
        const expandable = !!(section.items && section.items.length)
        const open = !!openMap[section.title]
        const sectionActive = Boolean(section.items?.some((item) => isPathActive(pathname, item.to)))
        const primaryItem = section.items?.[0]
        const badgeTotal = section.items?.reduce((total, item) => {
          const badge = item.badgeKey ? queue[item.badgeKey] : null
          return total + (typeof badge === 'number' && badge > 0 ? badge : 0)
        }, 0)
        const iconNode = Icon ? (
          <Badge
            color="primary"
            variant={badgeTotal ? 'standard' : 'dot'}
            badgeContent={badgeTotal || undefined}
            invisible={!badgeTotal && !sectionActive}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                minWidth: badgeTotal ? 16 : 7,
                height: badgeTotal ? 16 : 7,
                px: badgeTotal ? 0.5 : 0,
                fontSize: 10,
              },
            }}
          >
            <Icon fontSize="small" />
          </Badge>
        ) : null

        if (collapsed) {
          return (
            <Box key={section.title} sx={{ mb: 0.75 }}>
              <Tooltip title={section.title} placement="right" arrow>
                <ListItemButton
                  dense
                  component={primaryItem ? NavLink : 'button'}
                  to={primaryItem?.to}
                  selected={sectionActive}
                  aria-label={section.title}
                  sx={(theme) => {
                    const isDark = theme.palette.mode === 'dark'
                    return {
                      width: 48,
                      height: 44,
                      mx: 'auto',
                      px: 0,
                      justifyContent: 'center',
                      borderRadius: '16px',
                      color: sectionActive
                        ? isDark ? '#ffe29b' : 'primary.main'
                        : isDark ? 'rgba(255,246,220,0.68)' : 'rgba(33,24,47,0.64)',
                      border: sectionActive
                        ? isDark ? '1px solid rgba(240,198,106,0.44)' : '1px solid rgba(91,63,214,0.2)'
                        : '1px solid transparent',
                      bgcolor: sectionActive ? isDark ? 'rgba(240,198,106,0.13)' : 'rgba(91,63,214,0.08)' : 'transparent',
                      boxShadow: sectionActive
                        ? isDark
                          ? '0 12px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.1)'
                          : '0 12px 26px rgba(73,47,25,0.1), inset 0 1px 0 rgba(255,255,255,0.85)'
                        : 'none',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(240,198,106,0.1)' : 'rgba(91,63,214,0.08)',
                        color: isDark ? '#ffe29b' : 'primary.main',
                      },
                      '&.Mui-selected': {
                        bgcolor: isDark ? 'rgba(240,198,106,0.13)' : 'rgba(91,63,214,0.08)',
                        '&:hover': { bgcolor: isDark ? 'rgba(240,198,106,0.16)' : 'rgba(91,63,214,0.12)' },
                      },
                    }
                  }}
                >
                  {iconNode}
                </ListItemButton>
              </Tooltip>
            </Box>
          )
        }

        return (
          <Box key={section.title} sx={{ mb: 0.5 }}>
            <ListItemButton
              dense
              onClick={() => expandable && toggle(section.title)}
              sx={(theme) => {
                const isDark = theme.palette.mode === 'dark'
                return {
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  mx: 1,
                  color: sectionActive
                    ? isDark ? '#ffe29b' : 'primary.main'
                    : isDark ? 'rgba(255,246,220,0.76)' : 'rgba(33,24,47,0.72)',
                  border: sectionActive
                    ? isDark ? '1px solid rgba(240,198,106,0.3)' : '1px solid rgba(91,63,214,0.18)'
                    : '1px solid transparent',
                  bgcolor: sectionActive ? isDark ? 'rgba(240,198,106,0.08)' : 'rgba(91,63,214,0.07)' : 'transparent',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(240,198,106,0.1)' : 'rgba(91,63,214,0.08)',
                    color: isDark ? '#fff6dc' : 'primary.main',
                  },
                }
              }}
            >
              {Icon ? (
                <ListItemIcon
                  sx={(theme) => ({
                    minWidth: 40,
                    color: sectionActive
                      ? theme.palette.mode === 'dark' ? '#f0c66a' : 'primary.main'
                      : theme.palette.mode === 'dark' ? 'rgba(255,246,220,0.58)' : 'rgba(33,24,47,0.52)',
                  })}
                >
                  <Icon fontSize="small" />
                </ListItemIcon>
              ) : null}
              <ListItemText
                primary={
                  <Typography variant="overline" sx={{ letterSpacing: 1, fontWeight: 900 }}>
                    {section.title}
                  </Typography>
                }
              />
            </ListItemButton>

            {expandable ? (
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List dense disablePadding sx={{ pl: 1 }}>
                  {section.items.map((item) => {
                    const active = isPathActive(pathname, item.to)
                    const badge = item.badgeKey ? queue[item.badgeKey] : null
                    const showBadge = typeof badge === 'number' && badge > 0
                    return (
                      <ListItemButton
                        key={item.to}
                        component={NavLink}
                        to={item.to}
                        sx={(theme) => {
                          const isDark = theme.palette.mode === 'dark'
                          return {
                            pl: 4,
                            borderRadius: 2,
                            mx: 1,
                            mb: 0.25,
                            color: active
                              ? isDark ? '#fff6dc' : 'primary.main'
                              : isDark ? 'rgba(255,246,220,0.74)' : 'rgba(33,24,47,0.7)',
                            bgcolor: active ? isDark ? 'rgba(91,63,214,0.35)' : 'rgba(91,63,214,0.08)' : 'transparent',
                            border: active
                              ? isDark ? '1px solid rgba(240,198,106,0.18)' : '1px solid rgba(91,63,214,0.14)'
                              : '1px solid transparent',
                            '&:hover': {
                              bgcolor: active
                                ? isDark ? 'rgba(91,63,214,0.4)' : 'rgba(91,63,214,0.12)'
                                : isDark ? 'rgba(255,255,255,0.055)' : 'rgba(91,63,214,0.06)',
                            },
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">{item.label}</Typography>
                              {showBadge ? <Chip label={badge} size="small" sx={{ height: 20, fontSize: 11 }} /> : null}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    )
                  })}
                </List>
              </Collapse>
            ) : null}
          </Box>
        )
      })}
    </Box>
  )
}
